class ProductMonogramming extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.currentStep = "intro-1"
  }

  setupListeners () {
    super.setupListeners()

    this.els.stepChange.elements.forEach(el => {
      this.listeners.add(el, 'click', this.handleStepChange.bind(this))
    })

    this.listeners.add(this.els.form.element, 'submit', this.onSubmitHandler.bind(this))

    this.listeners.add(this.externalEls.trigger.element, 'click', this.setVariant.bind(this))
  }

  handleStepChange (e) {
    e.preventDefault()
    const trigger = e.target.closest('button')
    const step = trigger.dataset.step

    // Validation here

    this.currentStep = step
  }

  async onSubmitHandler (e) {
    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()

    if (this._isSubmitting) {
      return false
    }

    const hasSecondItem = this.externalEls['item-2-preview']?.element?.dataset.active === 'true'

    if (!hasSecondItem) {
      this.querySelectorAll('[data-step="intro-2"] input, [data-step="placement-2"] input').forEach(input => {
        input.disabled = true
      })
    }

    await this.addToCart()

    this.resetForm()

    return false
  }

  async addToCart () {

    if (this._isSubmitting) {
      return
    }

    this._isSubmitting = true

    const submitButton = this.querySelector('button[type="submit"]')
    if (submitButton) {
      submitButton.disabled = true
    }

    const uuid = new Date().valueOf()
    this.els.uuid.element.value = uuid

    const previews = this.takePreviewImages(uuid)

    try {
      const state = await window.BAO.Cart.addItemFromForm(this.els.form.element)

      // Not awaited: the preview is attached to the line once uploaded, so add to cart doesn't wait on it
      if (state && previews.length) this.attachPreviewImages(previews)

      return state
    } finally {
      this._isSubmitting = false
      if (submitButton) {
        submitButton.disabled = false
      }
    }
  }

  takePreviewImages (uuid) {
    const form = this.els.form.element
    const previewInputs = form.querySelectorAll('[name^="items["][name$="][_preview_image]"]')

    return Array.from(previewInputs).flatMap((input) => {
      const index = input.name.match(/items\[(\d+)\]/)?.[1]
      const base64 = input.value

      // Clear base64 so it isn't sent to Shopify
      input.value = ''

      // Disabled when the customer didn't add a second item
      if (!index || input.disabled) return []

      // Lets the line be found again after the add, as its key changes when properties change
      const lineUuid = `${uuid}-${index}`
      const uuidInput = form.querySelector(`[name="items[${index}]properties[_uuid]"]`)
      if (uuidInput) uuidInput.value = lineUuid

      return base64 ? [{ index, base64, lineUuid }] : []
    })
  }

  async attachPreviewImages (previews) {
    const uploads = previews.map(({ index, base64 }) =>
      this.uploadBase64ToWorker(base64)
        .then(result => result?.url)
        .catch((error) => {
          console.error(`[Monogramming] preview upload failed for item ${index}:`, error)
        }),
    )

    // Cart changes run one at a time so each sees the result of the last
    for (const [i, { index, lineUuid }] of previews.entries()) {
      const url = await uploads[i]
      if (!url) continue

      try {
        // Re-read the cart so a quantity changed or line removed during the upload is respected
        const cart = await fetch('/cart.js').then(response => response.json())
        const line = cart.items.find(item => item.properties?._uuid === lineUuid)
        if (!line) continue

        const state = await window.BAO.Cart.updateItemProperties(line.key, line.quantity, {
          ...line.properties,
          _Preview: url,
        })

        if (state?.sections) this.renderPushCartSections(state.sections)
      } catch (error) {
        console.error(`[Monogramming] preview attach failed for item ${index}:`, error)
      }
    }
  }

  renderPushCartSections (stateSections) {
    const sections = window.BAO.sectionRenderer.pushCartSections
      .filter(s => s.section in stateSections)
      .map(s => Object.assign({}, s, { html: stateSections[s.section] }))

    window.BAO.sectionRenderer.renderMultiple(sections)
  }

  async uploadBase64ToWorker (base64Data) {
    const blob = this.base64ToBlob(base64Data)
    const url = `https://olivia-von-halle.by-association-only.workers.dev/api/images?shop=${window.theme.permanentDomain}`

    const response = await fetch(url, {
      method: 'POST',
      body: blob,
      headers: { 'Content-Type': 'image/jpeg' },
    })

    if (!response.ok) {
      throw new Error(`Image upload failed with status: ${response.status}`)
    }

    return response.json()
  }

  base64ToBlob (base64Data, mimeType = 'image/jpeg') {
    const base64 = base64Data.replace(/^data:image\/[a-z]+;base64,/, '')
    const byteCharacters = atob(base64)
    const byteNumbers = new Uint8Array(byteCharacters.length)

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }

    return new Blob([byteNumbers], { type: mimeType })
  }

  setVariant () {
    const selectedVariant = document.querySelector('#product-select').value
    if (!selectedVariant) return

    this.els.id.element.value = selectedVariant
    this.externalEls['parent-id'].elements.forEach(el => {
      el.value = selectedVariant
    })
  }

  get currentStep () {
    return this._currentStep
  }

  set currentStep (step) {
    this._currentStep = step

    this.els.step?.elements.map(el => {
      el.setAttribute('aria-current', el.dataset.step === step)
    })
  }

  resetForm () {
    window.BAO.dispatchEvent(window.BAO.EVENTS.PERSONALISATION.RESET)
    
    this.currentStep = 'intro-1'
  }
}

if (!customElements.get('product-monogramming')) {
  customElements.define('product-monogramming', ProductMonogramming)
}
